//imports
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { brands, solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import UnderlineHeading from "../../../../components/underlineHeading";
import Card from "../../../../components/card";
import MiniModalController from "../../../../components/modals/miniModal/miniModalController";
import PerfilAlumnoModal from "../../../academy/perfilAlumnoModal";
import EnvioMasivo from "../../../../components/whatsapp/envioMasivo";
import { StudentAttendanceClassDTO, StudentAttendanceClassStatus, StudentProgressAlertDTO, StudentProgressRowDTO } from "../../../../models/StudentProgressDTO";
import { StudentProgressViewProps } from "./interfaces";
import { fechaBonita } from "../../../../utils/fechaBonita";
import { horarioBonito } from "../../../../utils/horarioBonito";
import S, { WA_SUGERIDOS } from "./strings";
import PorReponerTab from "./porReponerTab";
import MultiSelect from "./multiSelect";
import { BotonWhatsappAlumno, useVeWappChat } from "../../../../components/whatsapp/whatsappAlumno";

//styles
import "../../../../components/filtrosRapidos/filtrosRapidos.css";
import "../academicCalendar/styles.css";
import "./styles.css";

const horasTexto = (n: number): string => {
	const num = Number(n) || 0;
	return Number.isInteger(num) ? String(num) : num.toFixed(1);
};

const entero = (n: any): string => String(Math.round(Number(n) || 0));

const ESTADO_HORARIO: { [id: number]: string } = { 0: "Sin iniciar", 2: "Fusionado", 3: "Terminado", 4: "Cerrando" };

/* "14:30" -> "2:30 pm". Si no se puede leer, se deja como vino. */
const horaBonita = (hhmm: string | null | undefined): string => {
	const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
	if (!m) return String(hhmm || "");
	const h = Number(m[1]);
	if (isNaN(h) || h > 23) return String(hhmm);
	const h12 = h % 12 === 0 ? 12 : h % 12;
	return `${h12}:${m[2]} ${h < 12 ? "am" : "pm"}`;
};

const ESTADO_CLASE: { [k in StudentAttendanceClassStatus]: { texto: string; clase: string } } = {
	presente: { texto: "Presente", clase: "waPill verde" },
	ausente: { texto: "Ausente", clase: "waPill oro" },
	sin_registro: { texto: "Sin registro", clase: "waPill suave sinRegistro" },
	cancelada: { texto: "Cancelada", clase: "waPill suave" },
	pendiente: { texto: "Pendiente", clase: "waPill suave" }
};

const pillClase = (c: StudentAttendanceClassDTO) => {
	const e = ESTADO_CLASE[c.status] || { texto: c.status, clase: "waPill suave" };
	return <span className={e.clase}>{e.texto}</span>;
};

/* "87.5" -> "87.5 %", "88" -> "88 %" */
const porcentajeTexto = (n: number | null | undefined): string => {
	const num = Number(n);
	if (n === null || n === undefined || isNaN(num)) return "";
	return (Number.isInteger(num) ? String(num) : num.toFixed(1)) + " %";
};

/* Nota con un decimal: 8.75 -> "8.8"; sin nota -> "N/D". */
const notaTexto = (n: number | null | undefined): string => {
	const num = Number(n);
	if (n === null || n === undefined || isNaN(num)) return S.gradeNA;
	return num.toFixed(1);
};

/* Verde lo bueno, suave lo informativo, oro lo que hay que mirar. */
const pillAlerta = (a: StudentProgressAlertDTO): string => {
	if (a.code === "completed") return "waPill verde";
	if (a.code === "graduating") return "waPill suave";
	return "waPill oro";
};

type EstadoNota = "aprobado" | "en_riesgo" | "sin_notas";

/*
	Aprobado (verde) SOLO si la nota aprueba Y la asistencia llega al mínimo; si no, En Riesgo (oro).
	Sin nota del Portal de Clase: Sin Notas (neutro).
*/
const estadoNotaDe = (r: StudentProgressRowDTO): EstadoNota => {
	const g = r.grade;
	if (!g || g.final === null || g.final === undefined || g.status === "sin_notas") return "sin_notas";
	const bajoMinimo = Array.isArray(r.alerts) && r.alerts.some(a => a.code === "below_minimum");
	const asistenciaOk = r.attendancePct !== null && r.attendancePct !== undefined
		? Number(r.attendancePct) >= Number(r.minAttendancePct || 0)
		: !bajoMinimo;
	return g.status === "aprobado" && asistenciaOk ? "aprobado" : "en_riesgo";
};

const ESTADO_NOTA: { [k in EstadoNota]: { texto: string; clase: string } } = {
	aprobado: { texto: S.gradeApproved, clase: "waPill verde chica" },
	en_riesgo: { texto: S.gradeAtRisk, clase: "waPill oro chica" },
	sin_notas: { texto: S.gradeNoGrades, clase: "waPill suave chica" }
};

/*
	"5218112345678" -> "+52 81123 45678". Se separa la lada de pais con el catalogo
	(la coincidencia mas larga) y el resto se parte en dos; no se valida nada.
*/
const telefonoBonito = (phone: string | null): string => {
	//Tal como está guardado: 10 dígitos son México (+52), 8 son Honduras (+504); con lada ya incluida se respeta.
	if (!phone) return "";
	const d = String(phone).replace(/\D/g, "");
	if (!d) return String(phone);
	if (d.length === 10) return `+52 ${d.slice(0, 2)} ${d.slice(2, 6)} ${d.slice(6)}`;
	if (d.length === 8) return `+504 ${d.slice(0, 4)} ${d.slice(4)}`;
	return "+" + d;
};

const StudentProgressView = ({
	sectionLoaded,
	campusName,
	idleLocked,
	summary,
	chips,
	filter,
	onFilter,
	includeInactive,
	onToggleInactive,
	includeInactiveStudents,
	onToggleInactiveStudents,
	structured,
	structuredOptions,
	onMentors,
	onCourses,
	onPeriods,
	onToggleSchedule,
	onClearStructured,
	search,
	onSearch,
	rows,
	totalFiltered,
	page,
	pageCount,
	pageSize,
	onPage,
	canExport,
	onExport,
	grades,
	onOpenGrades,
	onCloseGrades,
	profileRow,
	onOpenProfile,
	onCloseProfile,
	attendance,
	onOpenAttendances,
	onCloseAttendances,
	whatsapp,
	onOpenWhatsapp,
	onCloseWhatsapp,
	onPickTemplate,
	selected,
	onToggleSelected,
	onSelectMany,
	onClearSelected,
	bulkOpen,
	onOpenBulk,
	onCloseBulk,
	campusId,
	currencyCode,
	tab,
	onTab,
	recoveryOpen,
	onRecoveriesChanged
}: StudentProgressViewProps) => {
	//Hooks antes de cualquier return (reglas de React).
	const veWa = useVeWappChat();

	if (!sectionLoaded) return <></>;

	const desde = totalFiltered === 0 ? 0 : (page - 1) * pageSize + 1;
	const hasta = Math.min(page * pageSize, totalFiltered);
	const hayEstructurados = Boolean(structured.mentorIds.length || structured.courseIds.length || structured.periodIds.length || structured.scheduleIds.length);
	const hayAcotado = Boolean(structured.courseIds.length || structured.mentorIds.length);
	const waRow = whatsapp.row;
	const asisRow = attendance.row;
	const asisDetalle = attendance.detail;
	const notasRow = grades.row;
	const notasDetalle = grades.detail;
	const hayVisitas = Boolean(asisDetalle && asisDetalle.classes.some(c => !c.isHome));
	const llenar = (texto: string): string => !waRow ? texto : texto
		.replace(/\{nombre\}/g, (waRow.studentName || "").split(" ")[0])
		.replace(/\{curso\}/g, waRow.courseName || "")
		.replace(/\{horario\}/g, horarioBonito(waRow.scheduleName) || "")
		.replace(/\{plantel\}/g, campusName || "Washington Academy");
	const sugerido = WA_SUGERIDOS.find(t => t.key === whatsapp.template);
	//Nombre del horario de origen de una transferida: el tablero lo trae en otra fila o en los chips de horarios.
	const nombreHorario = (scheduleId: number): string => {
		const fila = rows.find(r => r.scheduleId === scheduleId);
		if (fila) return horarioBonito(fila.scheduleName);
		const chip = structuredOptions.schedules.find(s => s.id === scheduleId);
		return chip ? chip.name : S.otherSchedule;
	};
	//Casillas de WhatsApp: por alumna (una alumna con dos matrículas es una sola casilla marcada en ambas filas).
	const idsPagina: number[] = Array.from(new Set(rows.map(r => r.studentId)));
	const todasMarcadas = idsPagina.length > 0 && idsPagina.every(id => selected.includes(id));
	const estadoNotaDetalle: EstadoNota = notasDetalle
		? (notasDetalle.final === null || notasDetalle.final === undefined || notasDetalle.status === "sin_notas" ? "sin_notas" : notasRow ? estadoNotaDe({ ...notasRow, grade: { final: notasDetalle.final, min: notasDetalle.min, status: notasDetalle.status, hasDetail: true } }) : notasDetalle.status === "aprobado" ? "aprobado" : "en_riesgo")
		: "sin_notas";

	return (
		<div className="avanceContainer">
			<div className="calendarioEncabezado">
				<h2 className="pageHeader">Progreso Académico</h2>
				{campusName ? <span className="calendarioPlantel">{campusName}</span> : null}
				{
					!canExport ? null :
						<button
							type="button"
							className="btn btn-default waBotonSecundario avanceExportar"
							disabled={idleLocked || totalFiltered === 0}
							onClick={onExport}
						>
							<FontAwesomeIcon icon={solid("file-arrow-down")} />
							<span className="ps-2">{S.export}</span>
						</button>
				}
			</div>
			<div className="avanceResumen">
				{
					includeInactiveStudents
						? S.enrollments(summary.total)
						: includeInactive ? S.students(summary.total) : S.activeStudents(summary.total)
				}
				{" · "}{S.rotating(summary.rotating)}
				{" · "}{S.composite(summary.composite)}
				{" · "}{S.withAlert(summary.withAlert)}
				{summary.graduating > 0 ? <>{" · "}{S.graduating(summary.graduating)}</> : null}
			</div>
			<UnderlineHeading />

			<div className="avancePestanas" role="tablist">
				<button type="button" role="tab" className="avancePestana" aria-selected={tab === "progreso"} onClick={() => onTab("progreso")}>{S.tabProgress}</button>
				<button type="button" role="tab" className="avancePestana" aria-selected={tab === "reponer"} onClick={() => onTab("reponer")}>
					{S.tabRecoveries}{recoveryOpen > 0 ? <small>{recoveryOpen}</small> : null}
				</button>
			</div>

			{
				tab === "reponer"
					? <PorReponerTab campusId={campusId} moneda={currencyCode} idleLocked={idleLocked} onChanged={onRecoveriesChanged} />
					: <>
			<div className="waFiltros">
				<div className="waFiltrosFila">
					<div className="waBuscador">
						<FontAwesomeIcon icon={solid("magnifying-glass")} />
						<input
							type="search"
							value={search}
							placeholder={S.searchPlaceholder}
							disabled={idleLocked}
							onChange={e => onSearch(e.target.value)}
						/>
						{
							!search ? null :
								<button type="button" onClick={() => onSearch("")} aria-label={S.clearSearch}>
									<FontAwesomeIcon icon={solid("xmark")} />
								</button>
						}
					</div>
					<div className="waChips">
						{
							chips.map(chip => {
								const activo = filter === chip.llave;
								return (
									<button
										key={chip.llave}
										type="button"
										className={activo ? "waChipFiltro naranja activo" : "waChipFiltro naranja"}
										aria-pressed={activo}
										disabled={idleLocked}
										onClick={() => onFilter(chip.llave)}
									>
										{activo ? <FontAwesomeIcon icon={solid("check")} /> : null}
										{chip.texto} ({chip.cuenta})
									</button>
								);
							})
						}
						<button
							type="button"
							className={includeInactive ? "waChipFiltro inactivos activo" : "waChipFiltro inactivos"}
							aria-pressed={includeInactive}
							disabled={idleLocked}
							onClick={onToggleInactive}
						>
							<FontAwesomeIcon icon={includeInactive ? solid("toggle-on") : solid("toggle-off")} />
							{S.includeInactiveSchedules}
						</button>
						<button
							type="button"
							className={includeInactiveStudents ? "waChipFiltro inactivos activo" : "waChipFiltro inactivos"}
							aria-pressed={includeInactiveStudents}
							disabled={idleLocked}
							onClick={onToggleInactiveStudents}
						>
							<FontAwesomeIcon icon={includeInactiveStudents ? solid("toggle-on") : solid("toggle-off")} />
							{S.includeInactiveStudents}
						</button>
					</div>
					<span className="waCuenta">
						{includeInactiveStudents ? S.enrollments(totalFiltered) : S.students(totalFiltered)}
					</span>
				</div>

				<div className="avanceEstructurados">
					<MultiSelect label={S.mentor} allText={S.allMentors} options={structuredOptions.mentors} value={structured.mentorIds} onChange={onMentors} disabled={idleLocked} feminine />
					<MultiSelect label={S.course} allText={S.allCourses} options={structuredOptions.courses} value={structured.courseIds} onChange={onCourses} disabled={idleLocked} />
					<MultiSelect label={S.period} allText={S.allPeriods} options={structuredOptions.periods} value={structured.periodIds} onChange={onPeriods} disabled={idleLocked} />
					{
						!hayEstructurados ? null :
							<button type="button" className="waChipFiltro limpio avanceLimpiar" disabled={idleLocked} onClick={onClearStructured}>
								<FontAwesomeIcon icon={solid("xmark")} />
								{S.clear}
							</button>
					}
					<div className="avanceHorarios">
						<span>{S.schedules}</span>
						<div className="avanceHorariosChips">
							{
								structuredOptions.schedules.length === 0
									? <span className="avanceHorariosVacio">{hayAcotado ? S.noSchedules : S.pickToSeeSchedules}</span>
									: structuredOptions.schedules.map(s => {
										const activo = structured.scheduleIds.includes(s.id);
										return (
											<button
												key={s.id}
												type="button"
												className={activo ? "waChipFiltro chico activo" : "waChipFiltro chico"}
												aria-pressed={activo}
												disabled={idleLocked}
												onClick={() => onToggleSchedule(s.id)}
											>
												{activo ? <FontAwesomeIcon icon={solid("check")} /> : null}
												{s.name}
											</button>
										);
									})
							}
						</div>
					</div>
				</div>
			</div>

			{
				!veWa || selected.length === 0 ? null :
					<div className="wa-sel-barra avanceSeleccion">
						<span>{S.selectedStudents(selected.length)}</span>
						<button type="button" disabled={idleLocked} onClick={onOpenBulk}><FontAwesomeIcon icon={brands("whatsapp")} />{S.whatsappMany(selected.length)}</button>
						<button type="button" className="secundario" onClick={onClearSelected}>{S.clearSelection}</button>
					</div>
			}

			<Card>
				<div className="containerTable col-lg-12">
					{
						rows.length === 0
							? <div className="calendarioVacio">{S.emptyRows}</div>
							:
							<table className="specialTable avanceTabla">
								<thead>
									<tr>
										{
											!veWa ? null :
												<th className="avanceCasilla">
													<input type="checkbox" className="wa-sel-check" aria-label={S.selectAllPage} checked={todasMarcadas} disabled={idleLocked}
														onChange={e => onSelectMany(idsPagina, e.target.checked)} />
												</th>
										}
										<th>{S.colStudent}</th>
										<th>{S.colCourse}</th>
										<th>{S.colSchedule}</th>
										<th>{S.colType}</th>
										<th>{S.colHours}</th>
										<th>{S.colSessions}</th>
										<th>{S.colAttendance}</th>
										<th>{S.colAlert}</th>
										<th className="text-center">{S.colGrade}</th>
										<th className="text-center">{S.colActions}</th>
									</tr>
								</thead>
								<tbody>
									{
										rows.map((r: StudentProgressRowDTO) => {
											const completo = r.entitledHours > 0 && r.takenHours >= r.entitledHours;
											const porcentaje = r.entitledHours > 0 ? Math.min(100, Math.round((r.takenHours / r.entitledHours) * 100)) : 0;
											//"Bajo mínimo" ya sale como pastilla en Asistencia: en Alerta no se repite.
											const alertas = Array.isArray(r.alerts) ? r.alerts.filter(a => a.code !== "below_minimum") : [];
											const sinAlertas = alertas.length === 0;
											const horarioInactivo = r.scheduleStatusId != null && r.scheduleStatusId !== 1;
											const matriculaNoActiva = includeInactiveStudents && r.enrollmentStatus && r.enrollmentStatus !== "Activo";
											const bajoMinimo = Array.isArray(r.alerts) && r.alerts.some(a => a.code === "below_minimum");
											const estadoNota = ESTADO_NOTA[estadoNotaDe(r)];
											const elegida = selected.includes(r.studentId);

											return (
												<tr key={r.enrollmentId} className={elegida ? "elegida" : ""}>
													{
														!veWa ? null :
															<td className="avanceCasilla">
																<input type="checkbox" className="wa-sel-check" aria-label={S.selectRow} checked={elegida} disabled={idleLocked}
																	onChange={() => onToggleSelected(r.studentId)} />
															</td>
													}
													<td>
														<div className="avancePrincipal">{r.studentName}</div>
														<div className="avanceSecundario">{r.identityCode}</div>
													</td>
													<td>
														<div className="avancePrincipal">{r.courseName}</div>
														<div className="avanceSecundario">{[r.levelName, r.periodName].filter(Boolean).join(" · ")}</div>
													</td>
													<td>
														<div>{horarioBonito(r.scheduleName)}</div>
														{
															!r.isRotating || !r.authorizedSchedules || r.authorizedSchedules.length === 0 ? null :
																<div className="avanceSecundario">
																	{r.authorizedSchedules.map(s => horarioBonito(s.name)).join(" · ")}
																</div>
														}
														{
															!horarioInactivo ? null :
																<span className="waPill suave chica">{ESTADO_HORARIO[r.scheduleStatusId] || "No activo"}</span>
														}
														{
															!r.transferredFrom ? null :
																<div className="avanceSecundario avanceTransferida">
																	{S.transferredFrom} {r.transferredFrom.scheduleName ? horarioBonito(r.transferredFrom.scheduleName) : nombreHorario(r.transferredFrom.scheduleId)} · {fechaBonita(r.transferredFrom.date)}
																</div>
														}
													</td>
													<td className="avanceTipo">
														<span className={r.isRotating ? "waPill suave" : "waPill fijo"}>
															{r.isRotating ? "Rotativo" : "Fijo"}
														</span>
														{r.isComposite ? <span className="waPill ink chica">Compuesto</span> : null}
														{matriculaNoActiva ? <span className="waPill ink chica">{r.enrollmentStatus}</span> : null}
													</td>
													<td className="avanceHoras">
														<div className={completo ? "avanceHorasTexto completo" : "avanceHorasTexto"}>
															{horasTexto(r.takenHours)} / {horasTexto(r.entitledHours)} h
														</div>
														<div className="avanceBarra">
															<div
																className={completo ? "avanceBarraRelleno completo" : "avanceBarraRelleno"}
																style={{ width: `${porcentaje}%` }}
															/>
														</div>
													</td>
													<td className="avanceSesiones">
														{entero(r.sessionsTaken)} de {entero(r.sessionsTotal)}
														{r.sessionsRecovered ? <div className="avanceSecundario avanceRepuestas">{S.recovered(r.sessionsRecovered)}</div> : null}
														{r.recoveryOpen ? <div className="avanceSecundario avanceSemanas alerta">{r.recoveryOpen} {S.tabRecoveries.toLowerCase()}</div> : null}
													</td>
													<td className="avanceAsistencia">
														<div>{r.lastPresent ? fechaBonita(r.lastPresent) : S.never}</div>
														<div className={r.weeksAbsent >= 2 ? "avanceSecundario avanceSemanas alerta" : "avanceSecundario avanceSemanas"}>
															{r.weeksAbsent === 0 ? S.thisWeek : S.weeksAbsent(r.weeksAbsent)}
														</div>
														{
															r.attendancePct === null || r.attendancePct === undefined
																? (r.minAttendancePct ? <div className="avanceSecundario">{S.noneRequiredYet} · {S.minimum} {porcentajeTexto(r.minAttendancePct)}</div> : null)
																: <div className={bajoMinimo ? "avanceSecundario avancePorcentaje alerta" : "avanceSecundario avancePorcentaje"}>
																	{porcentajeTexto(r.attendancePct)} · {S.minimum} {porcentajeTexto(r.minAttendancePct)}
																</div>
														}
														{
															!bajoMinimo ? null :
																<span className="waPill oro chica">{S.belowMinimum} · {S.recovers} {entero(r.sessionsToRecover)}</span>
														}
													</td>
													<td className="avanceAlertas">
														{
															sinAlertas
																? <span className="waPill verde">{S.upToDate}</span>
																: alertas.map(a =>
																	<span key={a.code} className={pillAlerta(a)}>
																		{a.label}
																	</span>
																)
														}
													</td>
													<td className="text-center avanceNota">
														<button
															type="button"
															className="waBotonNota"
															title={S.gradesTitle}
															disabled={idleLocked}
															onClick={() => onOpenGrades(r)}
														>
															<b>{notaTexto(r.grade ? r.grade.final : null)}</b>
															<span className={estadoNota.clase}>{estadoNota.texto}</span>
														</button>
													</td>
													<td className="avanceAcciones">
														<button
															type="button"
															className="btn btn-default waBotonIcono"
															title={S.attendances}
															aria-label={S.attendances}
															disabled={idleLocked}
															onClick={() => onOpenAttendances(r)}
														>
															<FontAwesomeIcon icon={solid("clipboard-list")} />
														</button>
														<button
															type="button"
															className="btn btn-default waBotonIcono"
															title={S.profile}
															aria-label={S.profile}
															disabled={idleLocked}
															onClick={() => onOpenProfile(r)}
														>
															<FontAwesomeIcon icon={solid("id-card")} />
														</button>
														{veWa ? (
														<button
															type="button"
															className="btn btn-default waBotonIcono whatsapp"
															title={S.whatsapp}
															aria-label={S.whatsapp}
															disabled={idleLocked}
															onClick={() => onOpenWhatsapp(r)}
														>
															<FontAwesomeIcon icon={brands("whatsapp")} />
														</button>
														) : null}
													</td>
												</tr>
											);
										})
									}
								</tbody>
							</table>
					}
				</div>
			</Card>

			{
				pageCount <= 1 ? null :
					<div className="avancePaginacion">
						<span className="avancePaginaTexto">{desde} a {hasta} de {totalFiltered}</span>
						<button
							type="button"
							className="btn btn-default waBotonIcono"
							aria-label={S.pagePrev}
							disabled={idleLocked || page <= 1}
							onClick={() => onPage(page - 1)}
						>
							<FontAwesomeIcon icon={solid("chevron-left")} />
						</button>
						<span className="avancePaginaTexto">Página {page} de {pageCount}</span>
						<button
							type="button"
							className="btn btn-default waBotonIcono"
							aria-label={S.pageNext}
							disabled={idleLocked || page >= pageCount}
							onClick={() => onPage(page + 1)}
						>
							<FontAwesomeIcon icon={solid("chevron-right")} />
						</button>
					</div>
			}

					</>
			}

			<div className="avanceNotasModal">
				<MiniModalController
					title={S.gradesTitle}
					show={notasRow !== null}
					onClose={onCloseGrades}
				>
					<div className="avanceModal">
						<div className="avanceModalAlumno">{notasRow ? notasRow.studentName : ""}</div>
						{
							!notasRow ? null :
								<div className="avanceAsisHorario">{notasRow.courseName} · {horarioBonito(notasRow.scheduleName)}</div>
						}
						{
							grades.loading
								? <div className="avanceModalTexto">{S.gradesLoading}</div>
								: grades.error || !notasDetalle
									? <div className="avanceModalAviso">{grades.error || S.noData}</div>
									:
									<>
										<div className="avanceNotaResumen">
											<div className="avanceNotaFinal">
												<span className="avanceNotaEtiqueta">{S.gradesFinal}</span>
												<b>{notaTexto(notasDetalle.final)}</b>
											</div>
											<span className={ESTADO_NOTA[estadoNotaDetalle].clase}>{ESTADO_NOTA[estadoNotaDetalle].texto}</span>
											<span className="avanceSecundario">{S.gradesMin} {notaTexto(notasDetalle.min)}</span>
											{notasDetalle.scheme ? <span className="avanceSecundario">{notasDetalle.scheme}</span> : null}
										</div>
										{
											notasDetalle.categories.length === 0
												? <div className="avanceModalTexto">{S.gradesNoItems}</div>
												:
												<div className="avanceNotaCategorias">
													{
														notasDetalle.categories.map((c, i) =>
															<div key={i} className="avanceNotaCategoria">
																<div className="avanceNotaCategoriaCab">
																	<b>{c.name}</b>
																	<span className="avanceSecundario">{S.gradesWeight} {porcentajeTexto(c.weight)}</span>
																	<span className="avanceNotaPromedio">{S.gradesAvg} <b>{notaTexto(c.avg)}</b></span>
																</div>
																{
																	!c.items || c.items.length === 0
																		? <div className="avanceSecundario">{S.gradesNoItems}</div>
																		:
																		<table className="avanceAsisTabla avanceNotaTabla">
																			<thead>
																				<tr>
																					<th>{S.gradesItem}</th>
																					<th>{S.gradesDate}</th>
																					<th className="text-end">{S.gradesScore}</th>
																				</tr>
																			</thead>
																			<tbody>
																				{
																					c.items.map((it, j) =>
																						<tr key={j}>
																							<td>{it.name}</td>
																							<td className="avanceAsisFecha">{it.date ? fechaBonita(it.date) : ""}</td>
																							<td className="text-end avanceNotaValor">
																								{it.score === null || it.score === undefined ? "—" : <>{notaTexto(it.score)}{it.max ? <span className="avanceSecundario"> / {notaTexto(it.max)}</span> : null}</>}
																							</td>
																						</tr>
																					)
																				}
																			</tbody>
																		</table>
																}
															</div>
														)
													}
												</div>
										}
									</>
						}
					</div>
				</MiniModalController>
			</div>

			<div className="avanceAsistenciasModal">
				<MiniModalController
					title={`${S.attendances} · ${asisRow ? asisRow.studentName : ""}`}
					show={asisRow !== null}
					onClose={onCloseAttendances}
				>
					<div className="avanceModal">
						{
							attendance.loading
								? <div className="avanceModalTexto">{S.attendancesLoading}</div>
								: attendance.error || !asisDetalle
									? <div className="avanceModalAviso">{attendance.error || S.noData}</div>
									:
									<>
										<div className="avanceAsisEncabezado">
											<div className="avanceModalAlumno">{asisDetalle.courseName}</div>
											<div className="avanceAsisHorario">
												{horarioBonito(asisDetalle.scheduleName)}
												{asisDetalle.isRotating ? <span className="waPill suave chica">Rotativo</span> : null}
											</div>
										</div>
										<div className="avanceAsisTotales">
											<span><b>{asisDetalle.totals.presentes}</b> presentes</span>
											<span>·</span>
											<span><b>{horasTexto(asisDetalle.totals.horas)}</b> h</span>
											<span>·</span>
											<span><b>{asisDetalle.totals.ausentes}</b> ausentes</span>
											<span>·</span>
											<span><b>{asisDetalle.totals.sinRegistro}</b> sin registro</span>
											<span>·</span>
											<span><b>{asisDetalle.totals.pendientes}</b> pendientes</span>
										</div>
										{
											asisDetalle.classes.length === 0
												? <div className="avanceModalTexto">{S.noClasses}</div>
												:
												<div className="avanceAsisTablaWrap">
													<table className="avanceAsisTabla">
														<thead>
															<tr>
																<th>#</th>
																<th>Fecha</th>
																<th>Hora</th>
																{hayVisitas ? <th>Horario</th> : null}
																<th className="text-end">Horas</th>
																<th>Estado</th>
																<th>Nota</th>
															</tr>
														</thead>
														<tbody>
															{
																asisDetalle.classes.map(c =>
																	<tr key={c.classId} className={c.status === "pendiente" ? "pendiente" : ""}>
																		<td className="avanceAsisNum">{c.classNumber}</td>
																		<td className="avanceAsisFecha">{fechaBonita(c.date)}</td>
																		<td className="avanceAsisHora">{horaBonita(c.time)}</td>
																		{
																			!hayVisitas ? null :
																				<td>
																					{
																						c.isHome ? <span className="avanceSecundario">—</span> :
																							<>
																								<div>{horarioBonito(c.scheduleName)}</div>
																								<div className="avanceSecundario">Rotativo</div>
																							</>
																					}
																				</td>
																		}
																		<td className="text-end">{horasTexto(c.hours)}</td>
																		<td>
																			{pillClase(c)}
																			{c.compensatory ? <span className="avanceSecundario avanceAsisComp">Compensatoria</span> : null}
																		</td>
																		<td className="avanceAsisNota">{c.note || ""}</td>
																	</tr>
																)
															}
														</tbody>
													</table>
												</div>
										}
									</>
						}
					</div>
				</MiniModalController>
			</div>

			<MiniModalController
				title={S.whatsapp}
				show={waRow !== null}
				onClose={onCloseWhatsapp}
			>
				<div className="avanceModal">
					<div className="avanceModalAlumno">{waRow ? waRow.studentName : ""}</div>
					{waRow && waRow.phone ? <div className="avanceModalTelefono">{telefonoBonito(waRow.phone)}</div> : null}

					<div className="avancePlantillas">
						{WA_SUGERIDOS.map(t =>
							<label key={t.key} className={whatsapp.template === t.key ? "avancePlantilla activa" : "avancePlantilla"}>
								<input
									type="radio"
									name="whatsappTemplate"
									checked={whatsapp.template === t.key}
									onChange={() => onPickTemplate(t.key)}
								/>
								<div>
									<div className="avancePlantillaNombre">{t.name}</div>
									<div className="avancePlantillaPreview">{llenar(t.text)}</div>
								</div>
							</label>
						)}
					</div>

					<div className="avanceModalAcciones">
						{waRow ? <BotonWhatsappAlumno campusId={campusId} studentId={waRow.studentId} nombre={waRow.studentName} texto
							sugerido={sugerido ? llenar(sugerido.text) : null} /> : null}
					</div>
				</div>
			</MiniModalController>

			{profileRow ? <PerfilAlumnoModal studentId={profileRow.studentId} campusId={campusId} onClose={onCloseProfile} /> : null}

			{
				bulkOpen ? <EnvioMasivo campusId={campusId} studentIds={selected} origen="progreso" referencia={campusName || null}
					onClose={onCloseBulk} onEnviado={onClearSelected} /> : null
			}
		</div>
	);
}

export default StudentProgressView
